/**
 * AccountNewsSection — the account page's news panel: the latest items tagged
 * to this department (press + TekStak signals), the people from this org
 * chart who appeared in the news, a refresh, and a link to the full feed.
 */

import React from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { RefreshCw, ExternalLink, Link2, Plus } from 'lucide-react';
import apiClient from '../../lib/apiClient';
import NewsItemRow, { fmtWhen } from '../NewsFeed/NewsItemRow';
import '../NewsFeed/NewsFeed.css';

export function useAccountNews(account, limit = 8) {
  return useQuery({
    queryKey: ['department-news', 'account', account, limit],
    queryFn: () => apiClient.get(`/api/v1/department-news/departments/${encodeURIComponent(account)}`, { query: { limit, since: '180d' } }),
    enabled: !!account,
    staleTime: 60_000,
  });
}

/**
 * `maxItems` clips the RENDERED list without touching the query — the board
 * widget uses it to draw only the stories that fit its cell and state the rest,
 * rather than scrolling. It must not change `limit`: that is part of the query
 * key, so narrowing it would refetch on every resize.
 */
export default function AccountNewsSection({ account, onContactClick, limit, maxItems }) {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  // The board passes a smaller limit than the tab: 8 stories measured 955px
  // against a 472px cell.
  const q = useAccountNews(account, limit || 8);
  const [notice, setNotice] = React.useState(null);
  const refresh = useMutation({
    mutationFn: () => apiClient.post(`/api/v1/department-news/refresh/${encodeURIComponent(account)}`, {}),
    onMutate: () => setNotice(null),
    onError: (e) => setNotice({ kind: 'err', text: e?.message || 'Refresh failed.' }),
    onSuccess: (r) => {
      const added = (r?.gnews?.added || 0) + (r?.rss?.added || 0);
      setNotice({ kind: 'ok', text: added ? `Pulled ${added} new item(s).` : 'Nothing new upstream.' });
      queryClient.invalidateQueries({ queryKey: ['department-news'] });
    },
  });

  // Paste-a-link: file a story to this account. Title is optional — the
  // backend reads the page's headline; if it can't, the title field appears.
  const [link, setLink] = React.useState('');
  const [linkTitle, setLinkTitle] = React.useState('');
  const [needTitle, setNeedTitle] = React.useState(false);
  const submit = useMutation({
    mutationFn: () => apiClient.post('/api/v1/department-news/items', {
      url: link.trim(), title: linkTitle.trim() || undefined, departments: account, lane: 'department',
    }),
    onMutate: () => setNotice(null),
    onError: (e) => {
      const code = e?.data?.code || e?.code || '';
      const msg = e?.message || 'Could not file that link.';
      if (/NO_TITLE|PAGE_UNREADABLE|INVALID_TITLE/.test(code) || /title/i.test(msg)) setNeedTitle(true);
      setNotice({ kind: 'err', text: msg });
    },
    onSuccess: (r) => {
      setLink(''); setLinkTitle(''); setNeedTitle(false);
      setNotice({ kind: 'ok', text: r?.merged ? 'Already in the feed — tagged it to this account.' : `Filed: ${r?.item?.title || 'story'}` });
      queryClient.invalidateQueries({ queryKey: ['department-news'] });
    },
  });
  const canSubmit = /^https?:\/\/\S+/i.test(link.trim()) && (!needTitle || linkTitle.trim().length >= 12) && !submit.isPending;

  if (!account) return null;
  const allItems = q.data?.items || [];
  const items = maxItems == null ? allItems : allItems.slice(0, Math.max(1, maxItems));
  const people = q.data?.people_in_news || [];

  return (
    <div className="nf-account">
      <div className="nf-account-bar">
        <span className="nf-account-refreshed">
          {q.data?.last_refreshed_at ? `Checked ${fmtWhen(q.data.last_refreshed_at)}` : 'Not yet checked'}
        </span>
        <button type="button" className="nf-btn nf-btn--sm" disabled={refresh.isPending} onClick={() => refresh.mutate()} title="Pull Google News + this agency's newsroom now">
          <RefreshCw size={12} className={refresh.isPending ? 'nf-spin' : ''} /> Refresh
        </button>
        <button type="button" className="nf-btn nf-btn--sm" onClick={() => navigate(`/news?scope=all&departments=${encodeURIComponent(account)}`)}>
          All news <ExternalLink size={11} />
        </button>
      </div>
      <form className="nf-paste" onSubmit={(e) => { e.preventDefault(); if (canSubmit) submit.mutate(); }}>
        <div className="nf-paste-row">
          <Link2 size={13} className="nf-paste-icon" />
          <input
            type="url"
            placeholder="Paste a link to file it here…"
            value={link}
            onChange={(e) => { setLink(e.target.value); if (needTitle && !e.target.value) setNeedTitle(false); }}
            aria-label="Story URL"
          />
          <button type="submit" className="nf-btn nf-btn--sm nf-btn--primary" disabled={!canSubmit} title="File this story to the account">
            {submit.isPending ? <RefreshCw size={12} className="nf-spin" /> : <Plus size={12} />} Add
          </button>
        </div>
        {needTitle && (
          <input
            type="text"
            className="nf-paste-title"
            placeholder="Headline (the page didn't give us one)"
            value={linkTitle}
            onChange={(e) => setLinkTitle(e.target.value)}
            aria-label="Story title"
            autoFocus
          />
        )}
      </form>
      {notice && <p className={`nf-notice nf-notice--${notice.kind}`}>{notice.text}</p>}
      {q.isLoading && <p className="aw-none">Loading news…</p>}
      {q.error && <p className="nf-notice nf-notice--err">{q.error.message}</p>}
      {!q.isLoading && !q.error && items.length === 0 && (
        <p className="aw-none">Nothing tagged to this agency yet — Refresh pulls Google News and its newsroom now.</p>
      )}
      {people.length > 0 && (
        <div className="nf-people">
          <span className="nf-people-label">In the news:</span>
          {people.slice(0, 6).map((p) => (
            <button key={p.contact_id} type="button" className="nf-chip nf-chip--contact" title={p.title || ''} onClick={() => onContactClick?.(p)}>
              {p.name}{p.items > 1 ? ` ×${p.items}` : ''}
            </button>
          ))}
        </div>
      )}
      <div className="nf-strip-list">
        {items.map((it) => (
          <NewsItemRow key={it.id} item={it} compact onContact={(c) => onContactClick?.(c)} onDepartment={() => {}} />
        ))}
      </div>
    </div>
  );
}
